<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import CanvasDialogModal from '@/components/dialogs/CanvasDialogModal.vue';
import DataTableStatusBadge from '@/components/tables/DataTableStatusBadge.vue';
import type { LocaleSchema } from '@/locales/index';
import type { Building } from '@/typings/buildings/Building';
import { getCanvasDialogImageUrl } from '@/utils/canvas';
import { formatLongDate } from '@/utils/date';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const props = defineProps<{
  building?: Building;
}>();

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
  'building-selected': [building: Building],
  'reset-building-modal': [],
}>();

const dialogElement = useTemplateRef<InstanceType<typeof CanvasDialogModal>>('dialogElement');

const imageIsLoading = ref(true);

const buildingAvailable = computed(() => {
  return props.building?.availableUnits && props.building?.availableUnits > 0;
});

function getBuildingAddress(building?: Building) {
  if(!building) {
    return '';
  }
  
  return `${building.no} ${building.localizedStreet}`;
}

function selectBuilding() {
  if (props.building) {
    emit('building-selected', props.building);
    open.value = false;
  }
}

function closeDialog() {
  open.value = false;
}

function resetDialogState() {
  emit('reset-building-modal');
  imageIsLoading.value = true;
}
</script>

<template>
  <CanvasDialogModal ref="dialogElement"
                     v-model:open="open"
                     class="canvasDialogModal_building"
                     :data-availability-status="building?.status"
                     @dialog-close-transition-ended="resetDialogState">
    <template #image>
      <template v-if="building?.buildingImageUrl">
        <img :src="getCanvasDialogImageUrl(building?.buildingImageUrl, 700)"
             :data-image-is-loading="imageIsLoading"
             alt=""
             @load="imageIsLoading = false">
      </template>
      <span v-else>Aucune image disponible</span>
    </template>

    <template #description>
      <hgroup>
        <h3 class="canvasDialogModal-heading">
          {{ `${t('browsingApp.buildings.buildingNumber')} ${building?.no}` }}
        </h3>
        <DataTableStatusBadge :status="(building?.status as string)"
                              class="statusBadge_canvasDialogModal"
                              :label="t(`browsingApp.statuses.buildingStatus.${building?.status}`)"
                              :data-status="building?.status" />
      </hgroup>
      <dl class="canvasDialogModal-descriptionList ">
        <div v-if="getBuildingAddress(building)" class="canvasDialogModal-descriptionItem canvasDialogModal-descriptionItem_fullWidth">
          <dt>{{ t('common.address') }}</dt>
          <dd>{{ getBuildingAddress(building) }}</dd>
        </div>
        <div v-if="building?.endDate" class="canvasDialogModal-descriptionItem">
          <dt>{{ t('common.handoverDate') }}</dt>
          <dd>{{ formatLongDate(building?.endDate, locale) }}</dd>
        </div>
        <div v-if="building?.availableUnits" class="canvasDialogModal-descriptionItem">
          <dt>{{ t('browsingApp.units.availableUnitCount') }}</dt>
          <dd>{{ building?.availableUnits ?? 0 }}</dd>
        </div>
      </dl>
    </template>

    <template #actions>
      <button v-if="buildingAvailable"
              class="btn btn_textWhite canvasDialogModal-button"              
              @click="selectBuilding">
        {{ t('browsingApp.canvas.selectBuilding') }}
      </button>
      <button v-else
              class="btn btn_secondary canvasDialogModal-closeButton"
              @click="closeDialog">
        {{ t('common.close') }}
      </button>
    </template>
  </CanvasDialogModal>
</template>